import { UserRole } from '@/enums/user-role';
import { Person } from '@/models/person';
import { TokenResponse } from '@/models/token-response';
import { AuthenticationService } from '@/services/authentication.service';
import { withStorageSync } from '@angular-architects/ngrx-toolkit';
import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { firstValueFrom } from 'rxjs';

interface AuthenticationState {
  currentUser: Person | null;
  accessToken: string | null;
  refreshToken: string | null;
  roles: UserRole[] | null;
}

const initialState: AuthenticationState = {
  currentUser: null,
  accessToken: null,
  refreshToken: null,
  roles: null,
};

export const AuthenticationStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withStorageSync({
    key: 'authentication',
    select: ({ accessToken, refreshToken, roles }) => ({
      accessToken,
      refreshToken,
      roles,
    }),
  }),
  withComputed(({ roles }) => ({
    isPlatformAdmin: computed(() => {
      return roles()?.includes('PlatformAdmin');
    }),
  })),
  withComputed(({ accessToken, currentUser, isPlatformAdmin, roles }) => ({
    isLoggedIn: computed(() => !!accessToken()),
    isPlatformPortalUser: computed(() => {
      const currentUserValue = currentUser();
      if (!currentUserValue) {
        return false;
      }
      const userRoles = roles() || [];
      return isPlatformAdmin() || userRoles.includes('AnyOrganisationAdmin') || userRoles.includes('AnyProjectAdmin');
    }),
  })),
  withMethods(({ accessToken, ...state }, authenticationService = inject(AuthenticationService)) => ({
    handleToken({ accessToken, refreshToken }: TokenResponse) {
      const roles = authenticationService.fetchRolesFromAccessToken(accessToken);
      patchState(state, { accessToken, refreshToken, roles });
    },
    isTokenExpired() {
      const token = accessToken();
      if (!token) {
        return true;
      }

      return authenticationService.isTokenExpired(token);
    },
  })),
  withMethods(({ refreshToken, ...state }, authenticationService = inject(AuthenticationService)) => ({
    async login(username: string, password: string) {
      const response = await firstValueFrom(authenticationService.login({ username, password }));
      state.handleToken(response);
    },

    async refresh() {
      const token = refreshToken();
      if (!token) {
        throw new Error('No refresh token available');
      }

      const response = await firstValueFrom(authenticationService.refresh(token));
      state.handleToken(response);
    },

    async logout() {
      patchState(state, initialState);
    },

    async updateCurrentUser() {
      try {
        const user = await firstValueFrom(authenticationService.currentPerson());
        patchState(state, { currentUser: user });
      } catch (error) {
        console.warn('Error fetching current user:', error);
        patchState(state, initialState);
      }
    },
  })),
  withMethods((state) => ({
    async retrieveToken() {
      if (!state.isLoggedIn()) {
        return null;
      }

      if (!state.isTokenExpired()) {
        return state.accessToken();
      }

      try {
        await state.refresh();
        return state.accessToken();
      } catch (error) {
        console.error('Failed to retrieve token', error);
        await state.logout();
      }

      return null;
    },
  })),
);
